import { StickerConfig } from "@/lib/sticker";
import { ModelConfig } from "@/lib/models";
import { calculateStickerSize3D } from "@/lib/sizeCalculator";
import { RefObject, useEffect, useState } from "react";
import { Euler, Matrix4, Mesh, Quaternion, Raycaster, Vector3 } from "three";

interface Decal {
  position: Vector3;
  rotation: Euler;
  depth: number;
  config: StickerConfig;
}

export const useDecal = (
  meshRef: RefObject<Mesh>,
  decalPoint: Vector3 | null,
  normal: Vector3 | null,
  stickerConfig?: StickerConfig,
  modelConfig?: ModelConfig,
) => {
  const [decal, setDecal] = useState<Decal | null>(null);

  useEffect(() => {
    if (
      !meshRef.current ||
      !decalPoint ||
      !normal ||
      !stickerConfig ||
      !modelConfig
    )
      return;

    // Decal mode
    // Original decal placement logic
    // --- 1. 计算局部位置和稳定的旋转 ---
    // 恢复使用最稳定的 `makeBasis` 方法来计算旋转，避免贴纸随机扭转。
    const inverseMatrix = new Matrix4()
      .copy(meshRef.current.matrixWorld)
      .invert();
    const localPosition = decalPoint.clone().applyMatrix4(inverseMatrix);

    const rotation = new Euler();
    const zAxis = normal.clone().normalize(); // 局部法线作为Z轴
    const tempUp =
      Math.abs(zAxis.y) > 0.999 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0);
    const xAxis = new Vector3().crossVectors(tempUp, zAxis).normalize();
    const yAxis = new Vector3().crossVectors(zAxis, xAxis).normalize();
    const rotationMatrix = new Matrix4().makeBasis(xAxis, yAxis, zAxis);
    rotation.setFromRotationMatrix(rotationMatrix);

    // --- 2. 修正动态深度计算逻辑 ---
    const raycaster = new Raycaster();

    // 使用新的统一尺寸计算系统
    const stickerSize3D = calculateStickerSize3D(stickerConfig, modelConfig);
    const decalWidth = stickerSize3D.width;
    const decalHeight = stickerSize3D.height;

    // 在贴纸的2D平面上定义采样点
    const samplePoints = [
      new Vector3(0, 0, 0),
      new Vector3(decalWidth / 2, decalHeight / 2, 0),
      new Vector3(-decalWidth / 2, decalHeight / 2, 0),
      new Vector3(decalWidth / 2, -decalHeight / 2, 0),
      new Vector3(-decalWidth / 2, -decalHeight / 2, 0),
    ];

    // 关键修正：计算出贴纸平面在“世界坐标系”中的变换矩阵。
    // 这是通过组合局部位置、局部旋转，然后乘以模型的全局世界矩阵得到的。
    const decalWorldMatrix = new Matrix4()
      .compose(
        localPosition,
        new Quaternion().setFromEuler(rotation),
        new Vector3(1, 1, 1),
      )
      .premultiply(meshRef.current.matrixWorld);

    // 关键修正：计算出光线在“世界坐标系”中应该发射的方向。
    // 这个方向就是贴纸平面的Z轴（法线方向）在世界坐标系中的表示，并取反使其朝向模型内部。
    const rayDirection = new Vector3()
      .setFromMatrixColumn(decalWorldMatrix, 2)
      .negate();

    let maxDistance = 0;

    samplePoints.forEach((point) => {
      // 关键修正：将每个2D采样点转换到其在世界坐标系中的3D位置，作为光线的起点。
      const rayOrigin = point.clone().applyMatrix4(decalWorldMatrix);

      raycaster.set(rayOrigin, rayDirection);
      const intersects = raycaster.intersectObject(meshRef.current, true);

      if (intersects.length > 0) {
        // 记录从贴纸平面到模型表面的最大距离
        maxDistance = Math.max(maxDistance, intersects[0].distance);
      }
    });

    // 最终深度 = 探测到的最大曲面深度 + 一个小的安全缓冲值
    const calculatedDepth = maxDistance + 0.02;

    // --- 3. 设置最终的贴纸状态 ---
    // 同样增加一个微小的位置偏移，以防止Z-fighting（闪烁）
    const offsetDistance = 0.01;
    const offset = normal.clone().normalize().multiplyScalar(offsetDistance);
    const finalPosition = localPosition.clone().add(offset);

    console.log("depth:", calculatedDepth);

    setDecal({
      position: finalPosition,
      rotation: rotation,
      depth: calculatedDepth,
      config: stickerConfig,
    });
  }, [meshRef, decalPoint, normal, stickerConfig, modelConfig]);

  return decal;
};
